import { API, Auth } from 'aws-amplify';
import axios from 'axios';

// Deployment-safety bridge: this module hardcoded its Twilio API URL before
// REACT_APP_TWILIO_API_URL existed. If the variable is unset in some environment (a real risk —
// it isn't in this repo's own web/.env yet), fall back to that same hardcoded URL so live calling
// degrades to today's working behavior instead of breaking outright, while still warning loudly
// so the gap gets fixed. Delete this fallback once REACT_APP_TWILIO_API_URL is confirmed set in
// every environment.
const TWILIO_API_URL = (() => {
  if (process.env.REACT_APP_TWILIO_API_URL) {
    return process.env.REACT_APP_TWILIO_API_URL;
  }
  console.warn(
    'REACT_APP_TWILIO_API_URL is not set. Falling back to the legacy hardcoded Twilio API URL ' +
      '(https://bq1o18bczk.execute-api.us-west-1.amazonaws.com/dev). Set REACT_APP_TWILIO_API_URL ' +
      'in this environment.'
  );
  return 'https://bq1o18bczk.execute-api.us-west-1.amazonaws.com/dev';
})();

const catchError = (e) => {
  if (e.response) {
    return e.response.data.message;
  }
  return e.message;
};

export const getProvisionedNumbers = async (stateId) => {
  return API.get('api', `/twilio/provision-numbers`).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const sendMessages = async ({ message, phones, taskId, type }) => {
  const body = {
    message,
    phones,
    taskId,
    attempt: 1,
    type,
  };
  return API.post('api', `/twilio/texting-task-initiator`, { body }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const initiateSurvey = async ({ taskId, type }) => {
  const body = { taskId, attempt: 1, type };
  return API.post('api', `/twilio/survey-task-initiator`, { body }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const initiateVoiceRecordingTask = async ({ taskId, type }) => {
  const body = { taskId, attempt: 1, type };
  return API.post('api', `/twilio/voice-recording-task-initiator`, { body }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const sendTestMessage = async ({ message, to, from, mediaUrl }) => {
  const body = {
    message,
    to,
    from,
    mediaUrl,
  };
  return API.post('api', `/twilio/test-text`, { body }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const sendTestSurvey = async ({ twilioFlowSid, from, fromPhoneTwilioSid, to }) => {
  const body = { twilioFlowSid, from, fromPhoneTwilioSid, to };
  return API.post('api', `/twilio/survey-test`, { body }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const sendTestVoiceRecording = async ({ to, from, mediaUrl }) => {
  const body = {
    to,
    from,
    mediaUrl,
  };
  return API.post('api', `/twilio/test-voice-recording`, { body }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const uploadMedia = async ({ media_file, bucket, folder }) => {
  const formData = new FormData();
  formData.append('file', media_file);
  if (bucket) {
    formData.append('bucket', bucket);
  }
  if (folder) {
    formData.append('folder', folder);
  }
  return API.post('api', `/twilio/upload-media`, {
    body: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const sendManualMessages = async ({
  orgId,
  userId,
  name,
  content,
  from,
  numbers,
  media_url,
  media_mime_type,
  media_file_name,
  media_size,
  manual_text_batch_id,
  type,
  send_at,
  task_billing_status_id,
  task_id,
}) => {
  const body = {
    orgId,
    userId,
    name,
    content,
    from,
    numbers,
    media_url,
    media_mime_type,
    media_file_name,
    media_size,
    attempt: 1,
    manual_text_batch_id,
    type,
    send_at,
    task_billing_status_id,
    task_id,
  };
  return API.post('api', `/twilio/manual-text-task-initiator`, { body }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const upsertTwilioSmsSurvey = async (smsSurveyId) => {
  return API.post('api', `/twilio/survey-twilio-upsert`, { body: { smsSurveyId } }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const updateIncomingWebhookBySid = async ({ twilioSid }) => {
  const body = {
    twilioSid,
  };
  return API.post('api', `/twilio/update-incoming-webhook-by-sid`, { body }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const syncPhoneNumberMessagingServiceSIDs = async (phone_number_sids) => {
  return API.post('api', `/twilio/sync-number-service-sids`, {
    body: { phone_number_sids },
  }).catch((e) => {
    const error = catchError(e);
    throw new Error(error);
  });
};

export const getVoiceAccessToken = async ({ isPowerDialer = false } = {}) => {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  const res = await axios.get(
    `${TWILIO_API_URL}/twilio/generate-voice-token?is_power_dialer=${isPowerDialer}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return JSON.parse(res.data.body);
};

/**
 * Fires when the volunteer gives up waiting on the backend's answering-machine verdict and
 * leaves the voicemail themselves. Only meaningful on a power-dialer call.
 */
export const manualLeaveVoicemailForPowerDialer = async ({ agentCallSid }) => {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return axios.post(
    `${TWILIO_API_URL}/twilio/manual-leave-voicemail-power-dialer`,
    { agentCallSid },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

const orgIdHeaders = async () => {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  const selectedOrganization = localStorage?.getItem('organization-context');
  return {
    Authorization: `Bearer ${token}`,
    ...(selectedOrganization ? { 'x-selected-organization': selectedOrganization } : {}),
  };
};

export const uploadIncomingCallAudio = async ({ media_file }) => {
  const headers = await orgIdHeaders();
  const formData = new FormData();
  formData.append('file', media_file);
  return axios
    .post(
      `https://bq1o18bczk.execute-api.us-west-1.amazonaws.com/dev/twilio/upload-incoming-call-audio`,
      formData,
      {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      }
    )
    .then((res) => res.data)
    .catch((e) => {
      throw new Error(catchError(e));
    });
};

export const assignIncomingCallTwiml = async ({ orgId }) => {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return axios
    .post(
      `https://bq1o18bczk.execute-api.us-west-1.amazonaws.com/dev/twilio/assign-incoming-call-twiml`,
      { organization_id: orgId },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then((res) => res.data)
    .catch((e) => {
      throw new Error(catchError(e));
    });
};

export const deleteIncomingCallAudio = async () => {
  const headers = await orgIdHeaders();
  return axios
    .delete(
      `https://bq1o18bczk.execute-api.us-west-1.amazonaws.com/dev/twilio/delete-incoming-call-audio`,
      { headers }
    )
    .then((res) => res.data)
    .catch((e) => {
      throw new Error(catchError(e));
    });
};

/**
 * Starts the Nomorobo spam check now, rather than waiting for the daily run. Superadmin only.
 * It returns as soon as the check has started; the check itself takes about a minute.
 *
 * No x-selected-organization header on purpose: with one, the backend's roleCheck would judge
 * the caller's role in that org rather than their superadmin role.
 */
export const startSpamCheck = async () => {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return axios
    .post(
      `${TWILIO_API_URL}/twilio/check-spam-numbers`,
      {},
      { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
    )
    .then((res) => res.data)
    .catch((e) => {
      throw new Error(catchError(e));
    });
};

/**
 * Asks Nomorobo about one US number, in any common format. Superadmin only; nothing is stored.
 * Resolves to { phone_number, isSpam (true | false | null for no answer), score, neighborScore }.
 */
export const lookupSpamNumber = async (phoneNumber) => {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return axios
    .post(
      `${TWILIO_API_URL}/twilio/lookup-spam-number`,
      { phone_number: phoneNumber },
      { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
    )
    .then((res) => res.data)
    .catch((e) => {
      throw new Error(catchError(e));
    });
};

/**
 * What a manual UNSUB of these text replies' senders would cover, for the confirmation dialog.
 * Nothing is written. Resolves to { numbers: [{ senderNumber, receivingNumber, incomingSmsIds,
 * numberUnsubscribed, voters: [{ voter_id, state, first_name, last_name, unsubscribed }] }],
 * failed: [{ incomingSmsId, error }] } -- one entry per sender number, however many of its
 * replies were picked.
 */
export const previewRepliesUnsubscribe = async (incomingSmsIds) => {
  const headers = await orgIdHeaders();
  return axios
    .post(
      `${TWILIO_API_URL}/twilio/reply-unsubscribe`,
      { incomingSmsIds, action: 'preview' },
      { headers: { ...headers, 'Content-Type': 'application/json' } }
    )
    .then((res) => res.data)
    .catch((e) => {
      throw new Error(catchError(e));
    });
};

/**
 * Marks the confirmed voters ([{ voter_id, state }]) on these replies' numbers as unsubscribed,
 * as if they had texted STOP. Resolves to { inserted, numbers: [{ senderNumber, incomingSmsIds,
 * inserted, error? }], failed }.
 */
export const markRepliesUnsubscribe = async (incomingSmsIds, voters) => {
  const headers = await orgIdHeaders();
  return axios
    .post(
      `${TWILIO_API_URL}/twilio/reply-unsubscribe`,
      { incomingSmsIds, action: 'mark', voters },
      { headers: { ...headers, 'Content-Type': 'application/json' } }
    )
    .then((res) => res.data)
    .catch((e) => {
      throw new Error(catchError(e));
    });
};
